import { addKeyToFieldArrayItem } from "@sembark-travel/ui-form"
import type {
	TCancellationPolicy,
	TCancellationPolicyCondition,
	TCancellationPolicyPayload,
	TCancellationPolicyRule,
} from "./store"
import { formatDate, parseDate } from "@sembark-travel/datetime-utils"
import type {
	INewCancellationPolicyFormSchema,
	TCancellationConditionFormValues,
	TCancellationRuleFormValues,
} from "./NewItem"
import {
	diffDateRangeRules,
	diffListByKey,
	type TDiffRow,
	type TRuleBlockDiffEntry,
} from "../shared/policyDiffUtils"

export function policyToPayloadRules(
	rules: INewCancellationPolicyFormSchema["rules"]
): TCancellationPolicyPayload["rules"] {
	return (rules || []).map((rule) => ({
		valid_from: rule.valid_from
			? formatDate(rule.valid_from, "YYYY-MM-DD")
			: null,
		valid_to: rule.valid_to ? formatDate(rule.valid_to, "YYYY-MM-DD") : null,
		conditions: rule.conditions
			.map((c) => {
				return {
					min_days_before_checkin: Number(c.min_days_before_checkin),
					max_days_before_checkin: Number(c.max_days_before_checkin),
					cancellation_percentage: String(c.penalty_value),
				}
			})
			.sort((a, b) => a.min_days_before_checkin - b.min_days_before_checkin),
	}))
}

export function formValuesToPayload(
	values: INewCancellationPolicyFormSchema
): TCancellationPolicyPayload {
	return {
		name: values.name,
		additional_info: values.additional_info || undefined,
		rules: [
			...policyToPayloadRules([values.base_rule]),
			...policyToPayloadRules(values.rules),
		],
	}
}

export function policyVersionToFormValues(
	name: string,
	policy: TCancellationPolicy
): INewCancellationPolicyFormSchema {
	const baseRule = policy.rules.find(
		(r) => r.valid_from == null && r.valid_to == null
	)
	const dateRules = policy?.rules.filter(
		(r) => r.valid_from != null || r.valid_to != null
	)

	function ruleToFormValue(
		rule: TCancellationPolicyRule
	): TCancellationRuleFormValues {
		return addKeyToFieldArrayItem({
			valid_from: rule.valid_from ? parseDate(rule.valid_from) : null,
			valid_to: rule.valid_to ? parseDate(rule.valid_to) : null,
			conditions: rule.conditions.map(
				(c) =>
					addKeyToFieldArrayItem({
						min_days_before_checkin: c.min_days_before_checkin,
						max_days_before_checkin: c.max_days_before_checkin,
						penalty_value: Number(c.cancellation_percentage),
					}) as TCancellationConditionFormValues
			),
		}) as TCancellationRuleFormValues
	}

	return {
		name,
		additional_info: policy.additional_info || "",
		base_rule: baseRule
			? ruleToFormValue(baseRule)
			: {
					valid_from: null,
					valid_to: null,
					conditions: [
						addKeyToFieldArrayItem({
							// the first interval always starts at check-in, and its input
							// is disabled, so it has to be seeded here
							min_days_before_checkin: 0,
							max_days_before_checkin: undefined,
							penalty_value: undefined,
						}) as TCancellationConditionFormValues,
					],
				},
		rules: dateRules.map(ruleToFormValue),
	}
}

// The penalty timeline's colors. "No charge" is also the color of the open
// ended segment before the first interval, so both read it from here.
export const NO_PENALTY_COLOR = "#02EEB1"
const PARTIAL_PENALTY_COLOR = "#FFB459"
const FULL_PENALTY_COLOR = "#FF5561"

export function getSegmentColor(c: TCancellationConditionFormValues): string {
	const pct = Number(c.penalty_value || 0)
	if (pct === 0) return NO_PENALTY_COLOR
	if (pct <= 50) return PARTIAL_PENALTY_COLOR
	return FULL_PENALTY_COLOR
}

// ---------------------------------------------------------------------------
// Version diff
//
// Everything below is pure: no React, no XHR. The pairing and diffing is shared
// with payment policies (`shared/policyDiffUtils`); this only says how a condition
// is keyed and ordered, and `utils.spec.ts` covers the edge cases.
// `DiffDialog.tsx` only renders what these produce.
// ---------------------------------------------------------------------------

export type TPolicyRuleDiffEntry = TRuleBlockDiffEntry<
	TCancellationPolicyRule,
	TCancellationPolicyCondition
>

// Percentages arrive as strings and the same value can be spelled several ways
// ("100" / "100.00"). Comparing the number keeps those equal.
export function normalizePenaltyPercentage(
	value: string | number | null | undefined
): number {
	const parsed = Number(value)
	return isNaN(parsed) ? 0 : parsed
}

// The compact label used in the diff, e.g. "0-7d → 100%". The detail page
// spells the same thing out as a sentence, which is too long for two columns.
export function formatPolicyConditionLabel(
	condition: Pick<
		TCancellationPolicyCondition,
		| "min_days_before_checkin"
		| "max_days_before_checkin"
		| "cancellation_percentage"
	>
): string {
	const min = Number(condition.min_days_before_checkin || 0)
	const max = Number(condition.max_days_before_checkin || 0)
	const pct = normalizePenaltyPercentage(condition.cancellation_percentage)
	return `${min}-${max}d → ${pct}%`
}

// Identity of a condition for diffing purposes. `id` is deliberately left out:
// every edit writes fresh rows, so a condition carried over unchanged still
// gets a new id and would otherwise always read as removed + added.
export function policyConditionKey(
	condition: TCancellationPolicyCondition
): string {
	const min = Number(condition.min_days_before_checkin || 0)
	const max = Number(condition.max_days_before_checkin || 0)
	return `${min}-${max}-${normalizePenaltyPercentage(condition.cancellation_percentage)}`
}

// A total order, so the diff does not depend on the order the api happens to
// return conditions in. Note this is ascending, the reverse of the penalty
// timeline on the detail page, which reads latest-first.
export function sortPolicyConditions(
	conditions: ReadonlyArray<TCancellationPolicyCondition>
): Array<TCancellationPolicyCondition> {
	return [...conditions].sort((a, b) => {
		const minDiff =
			Number(a.min_days_before_checkin || 0) -
			Number(b.min_days_before_checkin || 0)
		if (minDiff !== 0) return minDiff
		const maxDiff =
			Number(a.max_days_before_checkin || 0) -
			Number(b.max_days_before_checkin || 0)
		if (maxDiff !== 0) return maxDiff
		return (
			normalizePenaltyPercentage(a.cancellation_percentage) -
			normalizePenaltyPercentage(b.cancellation_percentage)
		)
	})
}

export function diffPolicyConditions(
	previousConditions: ReadonlyArray<TCancellationPolicyCondition>,
	currentConditions: ReadonlyArray<TCancellationPolicyCondition>
): Array<TDiffRow<TCancellationPolicyCondition>> {
	return diffListByKey(
		sortPolicyConditions(previousConditions),
		sortPolicyConditions(currentConditions),
		policyConditionKey
	)
}

export function diffPolicyRules(
	previousRules: ReadonlyArray<TCancellationPolicyRule>,
	currentRules: ReadonlyArray<TCancellationPolicyRule>
): Array<TPolicyRuleDiffEntry> {
	return diffDateRangeRules(previousRules, currentRules, (previous, current) =>
		diffPolicyConditions(previous?.conditions || [], current?.conditions || [])
	)
}

export {
	diffMarkdownText,
	type TPolicyTextDiffEntry,
} from "../shared/policyDiffUtils"
